import { useEffect, useState } from 'react';
import styled from 'styled-components';
import SaveIcon from '../assets/save.png';
import PencilIcon from '../assets/colour.png';
import BackgroundIcon from '../assets/background.png';
import HandIcon from '../assets/hand.png';
import CustomButton from './Button';
import SaveActiveIcon from '../assets/save_active.png';
import PencilActiveIcon from '../assets/colour_active.png';
import BackgroundActiveIcon from '../assets/background_active.png';
import HandActiveIcon from '../assets/hand_active.png';
import { Colorful, ColorResult } from '@uiw/react-color';
import { hexToHsva } from '@uiw/color-convert';
import { Box, Typography } from '@mui/material';
import CanvasPreview from './CanvasPreview';
import ToolsSave from './ToolsSave';
import ToolsPencil, { ColourWrapper } from './ToolsPencil';

type TOOLS_TYPES = 'pencil' | 'hand' | 'save' | 'background';
const TOOLS: { [key in TOOLS_TYPES]: { icon: string; label: string; activeIcon: string } } = {
	pencil: { icon: PencilIcon, label: 'Pencil', activeIcon: PencilActiveIcon },
	hand: { icon: HandIcon, label: 'Hand', activeIcon: HandActiveIcon },
	save: { icon: SaveIcon, label: 'Save', activeIcon: SaveActiveIcon },
	background: { icon: BackgroundIcon, label: 'Background', activeIcon: BackgroundActiveIcon },
};

function Tools() {
	const [activeTool, setActiveTool] = useState<TOOLS_TYPES>('pencil');
	const [dragging, setDragging] = useState(false);
	const [bgHsva, setBgHsva] = useState({ h: 0, s: 0, v: 68, a: 1 });
	const [preview, setPreview] = useState(false);

	const p5 = () => window.editSketch; // helper to get the p5 instance of editSketch

	const handleActiveTool = (tool: TOOLS_TYPES) => {
		setActiveTool(tool);
		// if tool is hand or dragging, toggle mode
		if ((tool === 'hand' && !dragging) || (tool !== 'hand' && dragging)) {
			updateDragToggle();
		}
	};

	useEffect(() => {
		setBgHsva(hexToHsva(p5()?.getBackgroundColourHex()));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const updateBgColor = (color: ColorResult) => {
		setBgHsva(color.hsva);
		p5().setBackgroundColour(color.hexa);
	};

	const updateDragToggle = () => {
		p5()?.toggleMode();
		setDragging(s => !s);
	};

	// on preview open, toggle dragging mode so on backdrop click it wont draw
	const openPreview = () => {
		setPreview(true);
		if (activeTool !== 'hand') {
			updateDragToggle();
		}
	};

	// on preview close, toggle dragging mode back to drawing
	const closePreview = () => {
		setPreview(false);
		if (activeTool !== 'hand' && dragging) {
			updateDragToggle();
		}
	};

	return (
		<>
			<ToolsContainer>
				{activeTool === 'pencil' && <ToolsPencil p5={p5} />}

				{activeTool === 'background' && (
					<ColourWrapper>
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Specify Background Colour
						</Typography>
						<Colorful style={{ padding: 20, width: '100%' }} color={bgHsva} onChange={updateBgColor} />
					</ColourWrapper>
				)}

				{activeTool === 'hand' && (
					<Box flex={1}>
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Move Canvas
						</Typography>
						<Typography mx={2} my={1} fontSize={12}>
							Move the canvas around by dragging it with your finger.
						</Typography>
					</Box>
				)}

				{activeTool === 'save' && (
					<Box flex={1}>
						<Typography mx={2} mt={1} mb={0} component="h5" fontWeight={700} fontSize={12}>
							Save Image
						</Typography>

						<Buttons style={{ margin: '10px 0 15px' }}>
							<ToolsSave openPreview={openPreview} />
						</Buttons>
					</Box>
				)}
			</ToolsContainer>

			<Buttons>
				{Object.entries(TOOLS).map(([key, value]) => (
					<CustomButton key={key} imageSize={30} onClick={() => handleActiveTool(key as any)} image={key === activeTool ? value.activeIcon : value.icon} />
				))}
			</Buttons>

			{/* MODALS */}
			{preview && <CanvasPreview open={preview} onClose={closePreview} />}
		</>
	);
}

export default Tools;

const ToolsContainer = styled.div`
	background-color: #222;
	border-radius: 15px;
	padding: 5px 20px;
	margin: 15px 0;
`;

const Buttons = styled.div`
	display: flex;
	flew-direction: row;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 0 15px;
`;
